import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BlogService, BlogPost } from '../../../../core/services/blog.service';
import { Meta, Title } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { AnalyticsService, TrackingCategory } from '../../../../core/services/analytics.service';

interface BlogListState {
  category: TrackingCategory;
  searchQuery: string;
  currentPage: number;
}

@Component({
  selector: 'app-blog-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './blog-list.component.html',
  styleUrls: ['./blog-list.component.css']
})
export class BlogListComponent implements OnInit {
  // Make TrackingCategory available to template
  protected TrackingCategory = TrackingCategory;
  
  blogs$: Observable<BlogPost[]> = this.blogService.getBlogs();
  featuredPosts: (BlogPost | null)[] = [null];
  allPosts: BlogPost[] = [];
  posts: BlogPost[] = [];
  filteredPosts: BlogPost[] = [];
  selectedCategory: TrackingCategory = TrackingCategory.BLOG;
  searchQuery = '';
  currentPage = 1;
  postsPerPage = 10;
  totalPages = 1;
  categories: TrackingCategory[] = [
    TrackingCategory.BLOG,
    TrackingCategory.BLOG_FILTER,
    TrackingCategory.BLOG_SEARCH,
    TrackingCategory.BLOG_SOCIAL
  ];
  paginatedPosts: BlogPost[] = [];

  constructor(
    private blogService: BlogService,
    private title: Title,
    private meta: Meta,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.blogs$.subscribe(blogs => {
      this.allPosts = blogs;
      this.featuredPosts = blogs
        .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
        .slice(0, 3);
      this.posts = blogs;
      this.updateResults();
      this.updatePaginatedPosts();
      
      // Update meta description with latest post info
      if (this.featuredPosts.length > 0) {
        this.updateMetaDescription();
      }

      // Track page view with category data
      this.analytics.trackPageView({
        page_type: 'blog_list',
        total_posts: this.allPosts.length,
        categories: this.categories.join(','),
        data_stream: 'pub3'
      });
    });
  }

  private setupSEO(): void {
    // Set basic meta tags
    this.title.setTitle('Industry Intelligence Blog - Latest Insights and Updates');
    this.meta.updateTag({ 
      name: 'description', 
      content: 'Stay informed with the latest industry insights, market trends, and expert analysis from Industry Intelligence. Discover thought leadership content on technology, innovation, and digital transformation.'
    });

    // Set Open Graph tags
    this.meta.updateTag({ property: 'og:title', content: 'Industry Intelligence Blog' });
    this.meta.updateTag({ 
      property: 'og:description', 
      content: 'Expert insights on industry trends, technology, and digital transformation. Stay ahead with Industry Intelligence.'
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: 'https://industryintel.com/blog' });
    this.meta.updateTag({ 
      property: 'og:image', 
      content: 'https://industryintel.com/assets/images/blog-cover.jpg'
    });

    // Set Twitter Card tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: 'Industry Intelligence Blog' });
    this.meta.updateTag({ 
      name: 'twitter:description', 
      content: 'Expert insights on industry trends, technology, and digital transformation. Stay ahead with Industry Intelligence.'
    });
    this.meta.updateTag({ 
      name: 'twitter:image', 
      content: 'https://industryintel.com/assets/images/blog-cover.jpg'
    });

    // Set structured data for blog listing
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Industry Intelligence Blog',
      description: 'Expert insights and analysis on industry trends, technology, and digital transformation.',
      publisher: {
        '@type': 'Organization',
        name: 'Industry Intelligence Inc.',
        logo: {
          '@type': 'ImageObject',
          url: 'https://industryintel.com/assets/images/logo/i2-logo.svg'
        }
      },
      url: 'https://industryintel.com/blog'
    };

    const scriptElement = document.createElement('script');
    scriptElement.type = 'application/ld+json';
    scriptElement.text = JSON.stringify(structuredData);
    document.head.appendChild(scriptElement);
  }

  private updateMetaDescription(): void {
    const latestPost = this.featuredPosts[0];
    if (!latestPost) return;

    const description = `Latest from Industry Intelligence: ${latestPost.title}. Browse our expert insights on industry trends, technology, and digital transformation.`;
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ name: 'twitter:description', content: description });
  }

  filterByCategory(category: TrackingCategory): void {
    this.selectedCategory = category;
    this.analytics.trackEvent(TrackingCategory.BLOG_FILTER, 'category', {
      category,
      results: this.filteredPosts.length
    });
    this.updateResults();
  }

  clearCategoryFilter(): void {
    this.selectedCategory = TrackingCategory.BLOG;
    this.analytics.trackEvent(TrackingCategory.BLOG_FILTER, 'clear_filter');
    this.updateResults();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.updateResults();
  }

  onSearch(): void {
    this.updateResults();

    // Track search
    if (this.searchQuery) {
      this.analytics.trackSearch(this.searchQuery, {
        results_count: this.filteredPosts.length,
        category: this.selectedCategory,
        data_stream: 'pub3'
      });
    }
  }

  updateResults(): void {
    this.filteredPosts = this.posts.filter(post => {
      const matchesCategory = this.selectedCategory === TrackingCategory.BLOG || 
                            post.category === this.selectedCategory;
      const matchesSearch = !this.searchQuery || 
        post.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(this.searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    this.analytics.trackEvent(TrackingCategory.BLOG_FILTER, 'results_update', {
      category: this.selectedCategory,
      search_query: this.searchQuery || '',
      results: this.filteredPosts.length
    });

    this.updatePaginatedPosts();
  }

  private updatePaginatedPosts(): void {
    const start = (this.currentPage - 1) * this.postsPerPage;
    const end = start + this.postsPerPage;
    this.paginatedPosts = this.filteredPosts.slice(start, end);
    this.totalPages = Math.ceil(this.filteredPosts.length / this.postsPerPage);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.analytics.trackEvent(TrackingCategory.BLOG_PAGINATION, 'prev', {
        page: this.currentPage,
        total_pages: this.totalPages
      });
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.analytics.trackEvent(TrackingCategory.BLOG_PAGINATION, 'next', {
        page: this.currentPage,
        total_pages: this.totalPages
      });
    }
  }

} 